import React, { useState, useEffect, useCallback } from 'react';
import { checkDevModeStatus, startDevModeWatcher } from '../services/devModeGuardService';
import DevModeGuardModal from '../components/common/DevModeGuardModal';

/**
 * Hook reutilizable para proteger cualquier vista contra Modo Desarrollador y Mock Locations.
 * 
 * Uso:
 * const { DevModeGuardComponent, isBlocked, checkNow } = useDevModeGuard({
 *   enabled: true, // o condicional (ej. user.role === 'inspector')
 *   onLogout: handleLogout
 * });
 * 
 * En el JSX:
 * <DevModeGuardComponent />
 */
export function useDevModeGuard({ enabled = true, onLogout = null } = {}) {
  const [isBlocked, setIsBlocked] = useState(false);

  // Verificación manual bajo demanda
  const checkNow = useCallback(async () => {
    if (!enabled) return false;
    const status = await checkDevModeStatus();
    const blocked = status.isDevMode || status.isMockLocation;
    setIsBlocked(blocked);
    return blocked;
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      setIsBlocked(false);
      return;
    }

    // Iniciar supervisión continua (cada 2 segundos y cada que la app vuelva a primer plano)
    const stopWatcher = startDevModeWatcher((status) => {
      const blocked = status.isDevMode || status.isMockLocation;
      setIsBlocked(blocked);
    }, 2000);

    return () => {
      stopWatcher();
    };
  }, [enabled]);

  // Componente ya conectado al estado del hook
  const DevModeGuardComponent = useCallback(() => {
    if (!enabled || !isBlocked) return null;
    return (
      <DevModeGuardModal
        isOpen={isBlocked}
        onResolved={() => setIsBlocked(false)}
        onLogout={onLogout}
      />
    );
  }, [enabled, isBlocked, onLogout]);

  return {
    isBlocked,
    setIsBlocked,
    checkNow,
    DevModeGuardComponent
  };
}

export default useDevModeGuard;
